import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Type, Save, RefreshCcw, Eye, MessageSquare, Image as ImageIcon, Plus, Trash2, Shuffle, Upload } from "lucide-react";
import { toast } from "sonner";

/**
 * Super-admin only — Landing-page customisation editor.
 *
 * Three tabs:
 *  · FONTS   → heading + body typography from a curated Google Fonts whitelist
 *  · SLOGANS → add/edit custom hero slogans + pick rotation mode
 *  · IMAGES  → add/upload custom hero images + pick rotation mode
 *
 * Rotation modes: weekly · 48h · random · fixed.
 * Built-in slogans/images are read-only fallbacks; admin additions live in the
 * `custom_slogans` / `custom_images` arrays on the settings doc.
 */
const TABS = [
  { id: "fonts", label: "Fonts", icon: Type },
  { id: "slogans", label: "Slogans", icon: MessageSquare },
  { id: "images", label: "Images", icon: ImageIcon },
];

const MODE_LABELS = {
  weekly: "Weekly (Mon-to-Mon, same for everyone)",
  "48h": "Every 48 hours (freshens twice a week)",
  random: "Random each visitor (feels alive)",
  fixed: "Fixed pick (choose one below)",
};

export default function AdminLandingSettings() {
  const [tab, setTab] = useState("fonts");
  const [cfg, setCfg] = useState(null);
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [sloganMode, setSloganMode] = useState("weekly");
  const [sloganIndex, setSloganIndex] = useState(null);
  const [customSlogans, setCustomSlogans] = useState([]);
  const [disabledSloganIdx, setDisabledSloganIdx] = useState([]);
  const [imageMode, setImageMode] = useState("weekly");
  const [imageIndex, setImageIndex] = useState(null);
  const [customImages, setCustomImages] = useState([]);
  const [disabledImageIdx, setDisabledImageIdx] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site-settings/landing");
      setCfg(data);
      setHeading(data.heading_font);
      setBody(data.body_font);
      setSloganMode(data.slogan_mode || "weekly");
      setSloganIndex(data.slogan_index ?? null);
      setCustomSlogans(data.custom_slogans || []);
      setDisabledSloganIdx(data.disabled_slogan_indexes || []);
      setImageMode(data.image_mode || "weekly");
      setImageIndex(data.image_index ?? null);
      setCustomImages(data.custom_images || []);
      setDisabledImageIdx(data.disabled_image_indexes || []);
      preloadFonts(Object.values(data.options.heading), Object.values(data.options.body));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load settings");
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = cfg && (
    heading !== cfg.heading_font
    || body !== cfg.body_font
    || sloganMode !== (cfg.slogan_mode || "weekly")
    || (sloganIndex ?? null) !== (cfg.slogan_index ?? null)
    || JSON.stringify(customSlogans) !== JSON.stringify(cfg.custom_slogans || [])
    || JSON.stringify([...disabledSloganIdx].sort()) !== JSON.stringify([...(cfg.disabled_slogan_indexes || [])].sort())
    || imageMode !== (cfg.image_mode || "weekly")
    || (imageIndex ?? null) !== (cfg.image_index ?? null)
    || JSON.stringify(customImages) !== JSON.stringify(cfg.custom_images || [])
    || JSON.stringify([...disabledImageIdx].sort()) !== JSON.stringify([...(cfg.disabled_image_indexes || [])].sort())
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        heading_font: heading,
        body_font: body,
        slogan_mode: sloganMode,
        slogan_index: sloganMode === "fixed" ? (sloganIndex ?? 0) : null,
        custom_slogans: customSlogans,
        disabled_slogan_indexes: disabledSloganIdx,
        image_mode: imageMode,
        image_index: imageMode === "fixed" ? (imageIndex ?? 0) : null,
        custom_images: customImages,
        disabled_image_indexes: disabledImageIdx,
      };
      const { data } = await api.put("/site-settings/landing", payload);
      setCfg(data);
      setDisabledSloganIdx(data.disabled_slogan_indexes || []);
      setDisabledImageIdx(data.disabled_image_indexes || []);
      toast.success("Landing settings saved — visit / to see the change");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (!cfg) return;
    setHeading(cfg.defaults.heading_font);
    setBody(cfg.defaults.body_font);
    setSloganMode("weekly"); setSloganIndex(null);
    setImageMode("weekly"); setImageIndex(null);
    // Custom lists stay — resetting doesn't nuke user data.
  };

  if (!cfg) {
    return <div className="label-mono text-[#A3A3A3]" data-testid="landing-settings-loading">Loading landing settings…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-landing-settings-page">
      <div>
        <Link to="/admin" className="text-xs uppercase tracking-wider text-[#A3A3A3] hover:text-[#CCFF00] inline-flex items-center gap-2 mb-3" data-testid="back-to-admin">
          <ArrowLeft className="w-3 h-3" /> Admin overview
        </Link>
        <div className="label-mono mb-2">LANDING PAGE CUSTOMISATION</div>
        <h1 className="h-display text-5xl tracking-tighter">Own your homepage.</h1>
        <p className="text-sm text-[#A3A3A3] mt-2 max-w-2xl">
          Pick the fonts, add your own hero slogans, swap in personal or external images.
          Choose how often each rotates — weekly, every 48 hours, random per visitor, or pinned to one pick.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#262626]" data-testid="landing-tabs">
        {TABS.map((t) => {
          const on = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className="px-4 py-2.5 text-xs uppercase tracking-wider inline-flex items-center gap-2 transition-colors"
              style={on
                ? { color: "#CCFF00", borderBottom: "2px solid #CCFF00", marginBottom: "-1px" }
                : { color: "#A3A3A3", borderBottom: "2px solid transparent", marginBottom: "-1px" }}
              data-testid={`landing-tab-${t.id}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "fonts" && (
        <div className="space-y-6" data-testid="tab-fonts">
          <div className="grid lg:grid-cols-2 gap-6">
            <FontPicker
              label="Heading font"
              testid="heading-font-picker"
              accent="#CCFF00"
              options={cfg.options.heading}
              value={heading}
              onChange={setHeading}
              previewText="MASTER THE KITCHEN."
            />
            <FontPicker
              label="Body font"
              testid="body-font-picker"
              accent="#FF4500"
              options={cfg.options.body}
              value={body}
              onChange={setBody}
              previewText="Squash & Pickleball coaching with proper progress tracking, structured development, and weekly sessions."
            />
          </div>

          <div className="card-surface p-6" data-testid="live-preview-card">
            <div className="flex items-center gap-2 mb-4">
              <Eye className="w-4 h-4 text-[#CCFF00]" />
              <div className="label-mono">Live preview</div>
            </div>
            <PreviewBlock heading={heading} body={body} />
          </div>
        </div>
      )}

      {tab === "slogans" && (
        <SlogansPanel
          builtin={cfg.builtin_slogans}
          custom={customSlogans}
          onCustomChange={setCustomSlogans}
          disabledIdx={disabledSloganIdx}
          onDisabledIdxChange={setDisabledSloganIdx}
          mode={sloganMode}
          onModeChange={setSloganMode}
          fixedIndex={sloganIndex}
          onFixedIndexChange={setSloganIndex}
        />
      )}

      {tab === "images" && (
        <ImagesPanel
          builtin={cfg.builtin_images}
          custom={customImages}
          onCustomChange={setCustomImages}
          disabledIdx={disabledImageIdx}
          onDisabledIdxChange={setDisabledImageIdx}
          mode={imageMode}
          onModeChange={setImageMode}
          fixedIndex={imageIndex}
          onFixedIndexChange={setImageIndex}
        />
      )}

      {/* Action bar */}
      <div className="flex flex-wrap gap-3 pt-4 border-t border-[#262626]">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-volt px-6 py-3 text-xs inline-flex items-center gap-2 disabled:opacity-30"
          data-testid="save-landing-settings"
        >
          <Save className="w-4 h-4" /> {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
        </button>
        <button
          onClick={resetToDefaults}
          className="px-5 py-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#A3A3A3] inline-flex items-center gap-2"
          data-testid="reset-landing-defaults"
        >
          <RefreshCcw className="w-3.5 h-3.5" /> Reset fonts + rotation
        </button>
        <Link
          to="/"
          target="_blank"
          rel="noopener"
          className="px-5 py-3 text-xs uppercase tracking-wider border border-[#262626] hover:border-[#CCFF00] hover:text-[#CCFF00] inline-flex items-center gap-2"
          data-testid="open-landing"
        >
          <Eye className="w-3.5 h-3.5" /> Open landing in new tab
        </Link>
      </div>

      {cfg.updated_at && (
        <div className="text-[10px] text-[#525252] uppercase tracking-wider">
          Last updated by {cfg.updated_by || "—"} on {new Date(cfg.updated_at).toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ---------- Slogans ----------
function SlogansPanel({ builtin, custom, onCustomChange, disabledIdx, onDisabledIdxChange, mode, onModeChange, fixedIndex, onFixedIndexChange }) {
  const [linesDraft, setLinesDraft] = useState(["", "", "", ""]);
  const disabledSet = useMemo(() => new Set(disabledIdx || []), [disabledIdx]);
  // "Rotation pool" = the entries that will actually be picked from at runtime.
  // Kept identical to the Landing.jsx merge order so the fixed-picker index
  // matches what the visitor sees.
  const merged = useMemo(() => (
    [
      ...(builtin || []).map((lines, i) => ({ lines, source: "builtin", localIdx: i, active: !disabledSet.has(i) })),
      ...(custom || []).map((s, i) => ({ lines: s.lines, source: "custom", localIdx: i, active: true })),
    ]
  ), [builtin, custom, disabledSet]);
  const activeMerged = useMemo(() => merged.filter((s) => s.active), [merged]);

  const addSlogan = () => {
    const lines = linesDraft.map((l) => l.trim()).filter(Boolean);
    if (!lines.length) { toast.error("Add at least one line"); return; }
    onCustomChange([...(custom || []), { lines }]);
    setLinesDraft(["", "", "", ""]);
    toast.success("Slogan added — click Save changes to publish");
  };

  const removeCustom = (idx) => {
    const next = [...(custom || [])];
    next.splice(idx, 1);
    onCustomChange(next);
    if (mode === "fixed" && fixedIndex !== null) {
      const activeAfter = activeMerged.length - 1;
      if (fixedIndex >= activeAfter) onFixedIndexChange(0);
    }
  };

  const toggleBuiltin = (i) => {
    const next = new Set(disabledSet);
    if (next.has(i)) next.delete(i); else next.add(i);
    const nextArr = Array.from(next).sort((a, b) => a - b);
    // Sanity: refuse to disable the last active entry.
    const remaining = merged.filter((s) => !(s.source === "builtin" && next.has(s.localIdx))).length;
    if (remaining === 0) {
      toast.error("Keep at least one slogan in the rotation.");
      return;
    }
    onDisabledIdxChange(nextArr);
  };

  return (
    <div className="space-y-6" data-testid="tab-slogans">
      <RotationPicker
        mode={mode}
        onModeChange={onModeChange}
        fixedIndex={fixedIndex}
        onFixedIndexChange={onFixedIndexChange}
        total={activeMerged.length}
        activeCount={activeMerged.length}
        totalCount={merged.length}
        testidPrefix="slogan"
        renderFixedLabel={(idx) => (
          <span className="text-white">
            {activeMerged[idx]?.lines.slice(0, 2).join(" ") || "—"}
          </span>
        )}
      />

      {/* Add custom slogan */}
      <div className="card-surface p-5" data-testid="add-custom-slogan">
        <div className="label-mono mb-3" style={{ color: "#CCFF00" }}>ADD A CUSTOM SLOGAN</div>
        <p className="text-xs text-[#A3A3A3] mb-4 leading-relaxed">
          Wrap a phrase in <code className="text-[#CCFF00]">{"{curly braces}"}</code> to paint it orange,
          or <code className="text-[#FF4500]">[square brackets]</code> for volt-green.
          Between 1 and 6 lines — 4 gives the most punch.
        </p>
        <div className="space-y-2 mb-3">
          {linesDraft.map((v, i) => (
            <input
              key={i}
              type="text"
              value={v}
              onChange={(e) => {
                const next = [...linesDraft]; next[i] = e.target.value; setLinesDraft(next);
              }}
              placeholder={`Line ${i + 1}${i === 1 ? " — try {ORANGE WORDS}" : i === 3 ? " — try [VOLT WORDS]" : ""}`}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none uppercase tracking-wider"
              data-testid={`slogan-line-${i}`}
              maxLength={60}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={addSlogan}
          className="px-4 py-2 text-xs uppercase tracking-wider bg-[#CCFF00] text-black hover:opacity-90 inline-flex items-center gap-2"
          data-testid="add-slogan-btn"
        >
          <Plus className="w-3.5 h-3.5" /> Add to rotation
        </button>
      </div>

      {/* List of slogans (builtin + custom) with include-toggle */}
      <div className="card-surface p-5" data-testid="slogan-list">
        <div className="flex items-center justify-between mb-4">
          <div className="label-mono">
            SLOGAN POOL · <span className="text-[#CCFF00]">{activeMerged.length}</span> in rotation · <span className="text-[#525252]">{merged.length} total</span>
          </div>
          <div className="text-[10px] text-[#525252]">Uncheck a built-in to hide it from rotation. Customs remove with the trash icon.</div>
        </div>
        <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
          {merged.map((s, idx) => {
            const disabled = !s.active;
            return (
              <div
                key={idx}
                className="flex items-start justify-between gap-3 border p-3 transition-opacity"
                style={{
                  borderColor: disabled ? "#262626" : "#262626",
                  opacity: disabled ? 0.5 : 1,
                }}
                data-testid={`slogan-entry-${idx}`}
                data-active={s.active}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="label-mono text-[#525252]">#{idx}</span>
                    <span
                      className="label-mono px-1.5 py-0.5"
                      style={{
                        background: s.source === "builtin" ? "#262626" : "#CCFF00",
                        color: s.source === "builtin" ? "#A3A3A3" : "#0A0A0A",
                      }}
                    >
                      {s.source === "builtin" ? "BUILT-IN" : "YOURS"}
                    </span>
                    {disabled && (
                      <span className="label-mono px-1.5 py-0.5 bg-[#FF4500] text-black">HIDDEN</span>
                    )}
                  </div>
                  <div className="text-sm leading-relaxed">
                    {s.lines.map((ln, i) => (
                      <div key={i}>{renderSloganPreview(ln)}</div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.source === "builtin" && (
                    <label
                      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-wider cursor-pointer select-none px-2 py-1"
                      style={{
                        borderColor: s.active ? "#CCFF00" : "#262626",
                        color: s.active ? "#CCFF00" : "#A3A3A3",
                      }}
                      data-testid={`toggle-builtin-slogan-${s.localIdx}`}
                    >
                      <input
                        type="checkbox"
                        checked={s.active}
                        onChange={() => toggleBuiltin(s.localIdx)}
                        className="accent-[#CCFF00] w-3.5 h-3.5"
                      />
                      Include
                    </label>
                  )}
                  {s.source === "custom" && (
                    <button
                      type="button"
                      onClick={() => removeCustom(s.localIdx)}
                      className="text-[#FF4500] hover:bg-[#FF4500]/10 p-2 transition-colors"
                      data-testid={`remove-slogan-${s.localIdx}`}
                      title="Remove this slogan"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Images ----------
function ImagesPanel({ builtin, custom, onCustomChange, disabledIdx, onDisabledIdxChange, mode, onModeChange, fixedIndex, onFixedIndexChange }) {
  const [urlDraft, setUrlDraft] = useState("");
  const [altDraft, setAltDraft] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const disabledSet = useMemo(() => new Set(disabledIdx || []), [disabledIdx]);

  const merged = useMemo(() => (
    [
      ...(builtin || []).map((img, i) => ({ ...img, source: "builtin", localIdx: i, active: !disabledSet.has(i) })),
      ...(custom || []).map((img, i) => ({ ...img, source: "custom", localIdx: i, active: true })),
    ]
  ), [builtin, custom, disabledSet]);
  const activeMerged = useMemo(() => merged.filter((i) => i.active), [merged]);

  const resolveSrc = (src) => src?.startsWith("/uploads/") ? `${backendUrl}${src}` : src;

  const addUrlImage = () => {
    const src = urlDraft.trim();
    if (!src) { toast.error("Paste an https:// URL first"); return; }
    if (src.startsWith("http://")) {
      toast.error("URL must be https:// (secure)"); return;
    }
    if (!src.startsWith("https://")) {
      toast.error("URL must start with https://"); return;
    }
    onCustomChange([...(custom || []), { src, alt: altDraft.trim() }]);
    setUrlDraft(""); setAltDraft("");
    toast.success("Image added — click Save changes to publish");
  };

  const onUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", altDraft.trim());
      const { data } = await api.post("/admin/landing/images/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onCustomChange([...(custom || []), data]);
      setAltDraft("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Uploaded — click Save changes to publish");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeCustom = (idx) => {
    const next = [...(custom || [])];
    next.splice(idx, 1);
    onCustomChange(next);
    if (mode === "fixed" && fixedIndex !== null) {
      const activeAfter = activeMerged.length - 1;
      if (fixedIndex >= activeAfter) onFixedIndexChange(0);
    }
  };

  const toggleBuiltin = (i) => {
    const next = new Set(disabledSet);
    if (next.has(i)) next.delete(i); else next.add(i);
    const remaining = merged.filter((im) => !(im.source === "builtin" && next.has(im.localIdx))).length;
    if (remaining === 0) {
      toast.error("Keep at least one image in the rotation.");
      return;
    }
    onDisabledIdxChange(Array.from(next).sort((a, b) => a - b));
  };

  return (
    <div className="space-y-6" data-testid="tab-images">
      <RotationPicker
        mode={mode}
        onModeChange={onModeChange}
        fixedIndex={fixedIndex}
        onFixedIndexChange={onFixedIndexChange}
        total={activeMerged.length}
        activeCount={activeMerged.length}
        totalCount={merged.length}
        testidPrefix="image"
        renderFixedLabel={(idx) => (
          <span className="inline-flex items-center gap-2">
            <img src={resolveSrc(activeMerged[idx]?.src)} alt="" className="w-8 h-8 object-cover" />
            <span className="text-white text-xs truncate">{activeMerged[idx]?.alt || "—"}</span>
          </span>
        )}
      />

      {/* Add via URL or upload */}
      <div className="card-surface p-5" data-testid="add-custom-image">
        <div className="label-mono mb-3" style={{ color: "#CCFF00" }}>ADD A HERO IMAGE</div>
        <p className="text-xs text-[#A3A3A3] mb-4 leading-relaxed">
          Paste any public <code className="text-[#CCFF00]">https://…</code> image URL (Unsplash, your own hosting, personal cloud share)
          or upload a file from your device. Best format: portrait 4:5, JPG/PNG/WEBP, under 5 MB.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-mono text-[10px] mb-1 block text-[#A3A3A3]">EXTERNAL URL</label>
            <input
              type="url"
              value={urlDraft}
              onChange={(e) => setUrlDraft(e.target.value)}
              placeholder="https://images.example.com/hero.jpg"
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
              data-testid="image-url-input"
            />
          </div>
          <div>
            <label className="label-mono text-[10px] mb-1 block text-[#A3A3A3]">ALT TEXT (both URL + upload)</label>
            <input
              type="text"
              value={altDraft}
              onChange={(e) => setAltDraft(e.target.value)}
              placeholder="Player smashing a third-shot drop"
              maxLength={200}
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
              data-testid="image-alt-input"
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            type="button"
            onClick={addUrlImage}
            className="px-4 py-2 text-xs uppercase tracking-wider bg-[#CCFF00] text-black hover:opacity-90 inline-flex items-center gap-2"
            data-testid="add-image-url-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Add URL to rotation
          </button>
          <label className="px-4 py-2 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black cursor-pointer inline-flex items-center gap-2">
            <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : "Upload from device"}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={onUploadFile}
              className="hidden"
              disabled={uploading}
              data-testid="image-upload-input"
            />
          </label>
        </div>
      </div>

      {/* Gallery */}
      <div className="card-surface p-5" data-testid="image-gallery">
        <div className="flex items-center justify-between mb-4">
          <div className="label-mono">
            IMAGE POOL · <span className="text-[#CCFF00]">{activeMerged.length}</span> in rotation · <span className="text-[#525252]">{merged.length} total</span>
          </div>
          <div className="text-[10px] text-[#525252]">Uncheck a built-in to hide it from rotation. Customs remove with the trash icon.</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[560px] overflow-y-auto pr-1">
          {merged.map((img, idx) => {
            const disabled = !img.active;
            return (
              <div
                key={idx}
                className="border transition-all"
                style={{ borderColor: "#262626", opacity: disabled ? 0.45 : 1 }}
                data-testid={`image-entry-${idx}`}
                data-active={img.active}
              >
                <div className="aspect-[4/5] bg-[#0A0A0A] relative">
                  <img src={resolveSrc(img.src)} alt={img.alt || ""} className="w-full h-full object-cover" loading="lazy" />
                  {disabled && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="label-mono px-2 py-1 bg-[#FF4500] text-black">HIDDEN</span>
                    </div>
                  )}
                </div>
                <div className="p-2 flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="label-mono text-[9px]" style={{ color: img.source === "builtin" ? "#525252" : "#CCFF00" }}>
                      #{idx} · {img.source === "builtin" ? "BUILT-IN" : "YOURS"}
                    </div>
                    <div className="text-[10px] text-[#A3A3A3] truncate">{img.alt || "—"}</div>
                  </div>
                  {img.source === "builtin" && (
                    <label
                      className="inline-flex items-center cursor-pointer select-none"
                      title={img.active ? "Hide from rotation" : "Include in rotation"}
                      data-testid={`toggle-builtin-image-${img.localIdx}`}
                    >
                      <input
                        type="checkbox"
                        checked={img.active}
                        onChange={() => toggleBuiltin(img.localIdx)}
                        className="accent-[#CCFF00] w-3.5 h-3.5"
                      />
                    </label>
                  )}
                  {img.source === "custom" && (
                    <button
                      type="button"
                      onClick={() => removeCustom(img.localIdx)}
                      className="text-[#FF4500] hover:bg-[#FF4500]/10 p-1.5 shrink-0"
                      data-testid={`remove-image-${img.localIdx}`}
                      title="Remove this image"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Rotation picker (shared between slogans + images) ----------
function RotationPicker({ mode, onModeChange, fixedIndex, onFixedIndexChange, total, activeCount, totalCount, testidPrefix, renderFixedLabel }) {
  const inRot = typeof activeCount === "number" ? activeCount : total;
  const totCount = typeof totalCount === "number" ? totalCount : total;
  return (
    <div className="card-surface p-5" data-testid={`${testidPrefix}-rotation-picker`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shuffle className="w-4 h-4 text-[#CCFF00]" />
          <div className="label-mono">ROTATION MODE</div>
        </div>
        <div className="label-mono text-[10px] text-[#A3A3A3]">
          <span className="text-[#CCFF00]">{inRot}</span> active · <span className="text-[#525252]">{totCount} total</span>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {Object.entries(MODE_LABELS).map(([m, label]) => {
          const on = mode === m;
          return (
            <button
              key={m}
              type="button"
              onClick={() => onModeChange(m)}
              className="text-left p-3 border transition-all"
              style={on
                ? { borderColor: "#CCFF00", background: "rgba(204,255,0,0.05)" }
                : { borderColor: "#262626" }}
              data-testid={`${testidPrefix}-mode-${m}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 border-2 shrink-0" style={on ? { borderColor: "#CCFF00", background: "#CCFF00" } : { borderColor: "#525252" }} />
                <div className="label-mono text-xs" style={{ color: on ? "#CCFF00" : "#A3A3A3" }}>{m.toUpperCase()}</div>
              </div>
              <div className="text-xs text-[#A3A3A3] leading-relaxed">{label}</div>
            </button>
          );
        })}
      </div>
      {mode === "fixed" && (
        <div className="mt-4 pt-4 border-t border-[#262626]" data-testid={`${testidPrefix}-fixed-picker`}>
          <label className="label-mono text-[10px] mb-2 block text-[#A3A3A3]">PICK ONE (from {inRot} active {testidPrefix}s)</label>
          <div className="flex items-center gap-3">
            <select
              value={fixedIndex ?? 0}
              onChange={(e) => onFixedIndexChange(parseInt(e.target.value, 10))}
              className="bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] px-3 py-2 text-sm outline-none"
              data-testid={`${testidPrefix}-fixed-index`}
            >
              {Array.from({ length: inRot }, (_, i) => (
                <option key={i} value={i}>#{i}</option>
              ))}
            </select>
            <div className="text-sm">{renderFixedLabel(fixedIndex ?? 0)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Font picker (unchanged; extracted to keep component small) ----------
function FontPicker({ label, testid, options, value, onChange, previewText, accent }) {
  const items = useMemo(() => Object.entries(options), [options]);
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="label-mono" style={{ color: accent }}>{label}</div>
        <div className="text-[10px] text-[#A3A3A3]">{items.length} options</div>
      </div>
      <div className="space-y-2 max-h-[440px] overflow-y-auto pr-1">
        {items.map(([name, meta]) => {
          const on = name === value;
          return (
            <button
              key={name}
              type="button"
              onClick={() => onChange(name)}
              className="w-full text-left p-3 border transition-all flex items-center gap-3"
              style={on
                ? { borderColor: accent, background: "rgba(204,255,0,0.05)" }
                : { borderColor: "#262626" }}
              data-testid={`${testid}-option-${slug(name)}`}
            >
              <div
                className="w-3 h-3 border-2 shrink-0"
                style={on ? { borderColor: accent, background: accent } : { borderColor: "#525252" }}
              />
              <div className="flex-1 min-w-0">
                <div
                  className="text-xl truncate"
                  style={{ fontFamily: `'${meta.family}', sans-serif` }}
                >
                  {previewText}
                </div>
                <div className="text-[10px] text-[#A3A3A3] mt-1 uppercase tracking-wider">
                  {name} · <span className="text-[#525252]">{meta.tag}</span>
                  {meta.default && <span className="ml-1 text-[#CCFF00]">· default</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PreviewBlock({ heading, body }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#262626] p-8 space-y-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]" style={{ fontFamily: `'${heading}', sans-serif` }}>
        Rally Lab · Pickleball coaching
      </div>
      <h2
        className="text-5xl leading-[0.95] uppercase tracking-tight"
        style={{ fontFamily: `'${heading}', sans-serif`, fontWeight: 700 }}
        data-testid="preview-heading"
      >
        Master the <span className="text-[#CCFF00]">{`{kitchen}`}</span>.<br />
        Win the <span className="text-[#FF4500]">[third shot]</span>.
      </h2>
      <p
        className="text-base text-[#A3A3A3] max-w-2xl leading-relaxed"
        style={{ fontFamily: `'${body}', sans-serif` }}
        data-testid="preview-body"
      >
        Proper progress tracking, structured development, and weekly sessions that actually move you up the ladder.
      </p>
    </div>
  );
}

// Renders {…}/[…] tokens the same way the live Landing hero does.
function renderSloganPreview(line) {
  const m1 = line.match(/^\{(.+?)\}(.*)$/);
  if (m1) return <><span style={{ color: "#FF4500" }}>{m1[1]}</span>{m1[2]}</>;
  const m2 = line.match(/^\[(.+?)\](.*)$/);
  if (m2) return <><span style={{ color: "#CCFF00" }}>{m2[1]}</span>{m2[2]}</>;
  return <>{line}</>;
}

const PRELOAD_FLAG = "__rallylab_font_picker_loaded";
function preloadFonts(headingMetas, bodyMetas) {
  if (window[PRELOAD_FLAG]) return;
  window[PRELOAD_FLAG] = true;
  const families = [...headingMetas, ...bodyMetas]
    .map((m) => `family=${encodeURIComponent(m.family)}:wght@${m.weights}`)
    .join("&");
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
  link.setAttribute("data-rallylab-font-picker", "1");
  document.head.appendChild(link);
}

function slug(s) {
  return String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}
